import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from "../Context/auth";
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import InsumosGrid from "../Components/insumosGrid/insumosGrid";
import Pagination from "../Components/Pagination/Pagination";
import Swal from 'sweetalert2';
import './Insumos.css';

function Insumos() {
    const { currentUser, appUserData } = useAuth();
    const [insumos, setInsumos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (appUserData && appUserData['ler_insumos'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    const buscarDados = useCallback(async (termoBusca = '') => {
        if (!currentUser) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            const url = new URL(`${API_URL}/api/insumos`);
            if (termoBusca) {
                url.searchParams.append('search', termoBusca);
            }
            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Falha ao buscar insumos.');
            const data = await response.json();
            setInsumos(data);
        } catch (error) {
            setErro('Ocorreu um erro ao carregar os insumos.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setTextoPesquisa(pesquisa);
        }, 500);

        return () => clearTimeout(timer);
    }, [pesquisa]);

    // Reset pagination when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [textoPesquisa]);

    useEffect(() => {
        buscarDados(textoPesquisa);
    }, [buscarDados, textoPesquisa, trigger]);

    const deleteInsumo = async (id) => {
        // Validation for Delete
        if (appUserData && appUserData['excluir_insumos'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const result = await Swal.fire({
            title: "Confirma a exclusão?",
            text: "O insumo e suas cores associadas serão removidos permanentemente.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#dc3545",
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (result.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/insumos/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Falha ao deletar insumo.');
                }

                setTrigger(Date.now());
                Swal.fire("Excluído!", "O registro foi apagado com sucesso.", "success");
            } catch (error) {
                console.error("Erro ao deletar o insumo: ", error);
                Swal.fire("Erro!", error.message, "error");
            }
        }
    };

    const handleAtualizar = () => {
        setPesquisa('');
        setTextoPesquisa('');
        setTrigger(Date.now());
    };

    const handlePesquisar = () => {
        setTextoPesquisa(pesquisa);
    };

    // Pagination Calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInsumos = insumos.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const changeItemsPerPage = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    return (
        <div className="insumos-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="insumos-header">
                    <h1 className="insumos-title">
                        <i className="fas fa-cubes text-primary"></i>
                        Listagem de <span className="highlight">Insumos</span>
                    </h1>
                </div>

                <main className="insumos-content">
                    <div className="insumos-controls">
                        <Link
                            to="/app/novoinsumo"
                            className="btn-primary-custom me-2"
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_insumos'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i> Novo Insumo
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por código, descrição, marca..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                            />
                        </div>
                        <button
                            className="btn-icon-custom"
                            onClick={handleAtualizar}
                            disabled={loading}
                            title="Atualizar Lista"
                        >
                            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>

                    {erro && (
                        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            <div>{erro}</div>
                        </div>
                    )}

                    <div className="insumos-grid-wrapper">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                                <p className="mt-2 text-muted">Carregando dados...</p>
                            </div>
                        ) : (
                            <InsumosGrid
                                arrayInsumos={currentInsumos}
                                clickDelete={deleteInsumo}
                                userPermissions={appUserData}
                            />
                        )}
                    </div>

                    {!loading && (
                        <Pagination
                            totalItems={insumos.length}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={paginate}
                            onItemsPerPageChange={changeItemsPerPage}
                        />
                    )}
                </main>
            </div>
        </div>
    );
}

export default Insumos;